import type { UserProfile } from '../types';
import {
  analyzeWeakSpots,
  formatKeyLabel,
  type WeakSpotsAnalysis,
} from '../lib/typingProfile';
import { useEnterAction } from '../lib/useEnterAction';

interface WeakSpotsIntroProps {
  user: UserProfile;
  onContinue: () => void;
  onCancel: () => void;
}

function StatPill({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center rounded-full bg-zinc-100 px-2.5 py-0.5 text-xs font-medium text-strong ring-1 ring-zinc-200 dark:bg-zinc-800/60 dark:ring-white/10">
      {children}
    </span>
  );
}

function KeysSection({ analysis }: { analysis: WeakSpotsAnalysis }) {
  return (
    <section className="surface p-5">
      <p className="label">Taster</p>
      {analysis.keys.length === 0 ? (
        <p className="mt-2 text-sm text-muted">Ingen svage taster lige nu.</p>
      ) : (
        <ul className="mt-3 flex flex-wrap gap-2">
          {analysis.keys.map((k) => (
            <li key={k.key}>
              <StatPill>
                {formatKeyLabel(k.key)}
                {k.errors >= 1 && ` · ${Math.round(k.errors)} fejl`}
                {k.avgMs !== null &&
                  k.avgMs > 650 &&
                  ` · ${Math.round(k.avgMs)} ms`}
              </StatPill>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function BigramsSection({ analysis }: { analysis: WeakSpotsAnalysis }) {
  return (
    <section className="surface p-5">
      <p className="label">Kombinationer</p>
      {analysis.bigrams.length === 0 ? (
        <p className="mt-2 text-sm text-muted">
          Ikke nok data om tast-overgange endnu.
        </p>
      ) : (
        <ul className="mt-3 flex flex-wrap gap-2">
          {analysis.bigrams.map((b) => (
            <li key={b.bigram}>
              <StatPill>
                {b.label}
                {b.errors >= 0.5 && ` · ${Math.round(b.errors)} fejl`}
                {b.avgMs !== null &&
                  b.avgMs > 400 &&
                  ` · ${Math.round(b.avgMs)} ms`}
              </StatPill>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function WordsSection({ analysis }: { analysis: WeakSpotsAnalysis }) {
  return (
    <section className="surface p-5">
      <p className="label">Ord</p>
      {analysis.words.length === 0 ? (
        <p className="mt-2 text-sm text-muted">
          Vi har endnu ikke nok ord-data — kombinationer og taster bruges i
          stedet.
        </p>
      ) : (
        <ul className="mt-3 flex flex-wrap gap-2">
          {analysis.words.map((w) => (
            <li key={w.word}>
              <StatPill>
                {w.word}
                {w.mistakes >= 0.5 && ` · ${Math.round(w.mistakes)} fejl`}
                {` · ${Math.round(w.avgMsPerChar)} ms/t`}
              </StatPill>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function TrainingPlan({ analysis }: { analysis: WeakSpotsAnalysis }) {
  const items: string[] = [];
  if (analysis.drillBigrams.length > 0) {
    items.push(
      `Kombinationer: ${analysis.drillBigrams.slice(0, 4).join(' · ')}`,
    );
  } else if (analysis.keys.length > 0) {
    items.push('Kombinationer: tilfældige par med svage taster');
  }
  if (analysis.drillWords.length > 0) {
    items.push(`Ord: ${analysis.drillWords.slice(0, 5).join(', ')}`);
  }

  if (items.length === 0) return null;

  return (
    <section className="surface p-5 ring-2 ring-indigo-200 dark:ring-indigo-500/30">
      <p className="label">Dagens træning</p>
      <ul className="mt-3 space-y-2 text-sm text-strong">
        {items.map((item) => (
          <li key={item} className="flex gap-2">
            <span className="text-indigo-500" aria-hidden>
              →
            </span>
            {item}
          </li>
        ))}
      </ul>
    </section>
  );
}

export default function WeakSpotsIntro({
  user,
  onContinue,
  onCancel,
}: WeakSpotsIntroProps) {
  const analysis = analyzeWeakSpots(user);
  useEnterAction(onContinue);

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-6 animate-fade-in-up">
      <header>
        <p className="label">Ekstra øvelse</p>
        <h1 className="mt-1 text-2xl font-semibold text-strong">
          Svage punkter
        </h1>
        <p className="mt-2 text-sm text-muted">
          Baseret på dine seneste lektioner — taster du fejler på, kombinationer
          der tager tid, og ord der driller.
        </p>
      </header>

      <KeysSection analysis={analysis} />
      <BigramsSection analysis={analysis} />
      <WordsSection analysis={analysis} />
      <TrainingPlan analysis={analysis} />

      <div className="flex flex-col gap-2 sm:flex-row">
        <button
          type="button"
          onClick={onContinue}
          className="flex-1 rounded-xl bg-indigo-500 px-4 py-3 font-semibold text-white shadow-sm transition hover:bg-indigo-400 dark:shadow-lg dark:shadow-indigo-500/30"
        >
          Start træning
        </button>
        <button type="button" onClick={onCancel} className="btn-secondary flex-1">
          Tilbage
        </button>
      </div>
      <p className="text-center text-xs text-faint">Tryk Enter for at starte</p>
    </div>
  );
}
